// The synthesiser's parameters for an ECG graphic: the rhythm, the rate and the ST shifts, and the measurements the question
// asks for (PR, QRS, QTc, axis, T inversion) when each is a number the synthesiser can draw. The API checks them before it
// serves a question (api/src/core/itemize.ts ecgMeasuresProblem); this is the phone's own guard, so a graphic from anywhere
// (an uploaded set, an old payload) still draws, at the defaults where a value is missing or makes no sense.
import type { EcgGraphic } from '@shared/types';
import type { EcgParams, Lead } from './types';
import { LEADS } from './synth';

/** The range each measurement is drawn in (ms, degrees). Keep in step with ECG_LIMITS in api/src/core/itemize.ts. */
export const ECG_LIMITS = { prMs: [80, 400], qrsMs: [60, 240], qtcMs: [300, 650], axisDeg: [-180, 180] } as const;

type Measure = keyof typeof ECG_LIMITS;
const MEASURES: readonly Measure[] = ['prMs', 'qrsMs', 'qtcMs', 'axisDeg'];
const isLead = (v: unknown): v is Lead => typeof v === 'string' && (LEADS as readonly string[]).includes(v);

/** The graphic's rhythm, rate, ST shifts and drawable measurements as the synthesiser takes them. Never an interpretation. */
export function ecgParams(g: EcgGraphic): EcgParams {
  const params: EcgParams = { rate: g.rate, rhythm: g.rhythm, stShift: {}, machineRead: [] };
  for (const [lead, mv] of Object.entries(g.stShift ?? {})) if (isLead(lead) && typeof mv === 'number' && Number.isFinite(mv)) params.stShift[lead] = mv;
  for (const k of MEASURES) {
    const v = (g as Partial<Record<Measure, unknown>>)[k];
    const [lo, hi] = ECG_LIMITS[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi) params[k] = v;
  }
  const inverted = Array.isArray(g.tInversion) ? [...new Set(g.tInversion.filter(isLead))] : [];
  if (inverted.length) params.tInversion = inverted;
  return params;
}

/** A key that changes whenever anything the trace is drawn from changes (for memos and effects). */
export const ecgKey = (g: EcgGraphic): string => JSON.stringify(ecgParams(g));
